/* ==========================================================================
   Socius Design — Taste engine (client-side recommendations)
   File:    assets/socius-taste.js
   Purpose: Adaptive product recommendations that learn from the visitor's
            browsing history, search queries, and collections viewed.
            Replaces the static "Pairs Well With" / "More from" rails with
            dynamic ones that re-rank a broader candidate pool against a
            taste profile built in the browser.

   Privacy: Explicit opt-in. Without consent, the engine runs in "cold start"
            mode — it still serves recommendations using Shopify's intent APIs
            and a curated bestsellers collection, but it does NOT track the
            visitor or write to localStorage. The consent banner lives in
            snippets/sc-consent-banner.liquid.

   Public surface: window.SocusTaste
     .hasConsent()                       → 'granted' | 'denied' | null
     .setConsent(decision)               → persist decision
     .trackView({ handle, id, tags, type, vendor, price, collections })
     .trackSearch(query)
     .trackCollection(handle)
     .profile()                          → aggregated taste profile
     .getRail({ intent, productId, limit, pins, fallbackCollection, productContext })
     .clearProfile()                     → wipe stored profile (consent keeps)
   ========================================================================== */
(function(){
  'use strict';

  var VERSION = '1.0.0';
  var LS = {
    CONSENT:     'sc_taste_consent',
    VIEWS:       'sc_taste_views',
    QUERIES:     'sc_taste_queries',
    COLLECTIONS: 'sc_taste_collections',
    BANNER_DISMISS: 'sc_taste_banner'
  };
  var LIMITS = {
    VIEWS:       40,
    QUERIES:     15,
    COLLECTIONS: 12
  };
  var QUERY_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

  /* ------------------------------------------------------------------------
     Storage helpers — silent failures (private-browsing, disabled storage).
  ------------------------------------------------------------------------ */
  function safeGet(key) {
    try {
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  function safeSet(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }
  function safeRemove(key) { try { localStorage.removeItem(key); } catch (e) {} }

  /* ------------------------------------------------------------------------
     Consent
  ------------------------------------------------------------------------ */
  function hasConsent() {
    var c = safeGet(LS.CONSENT);
    return c && c.decision ? c.decision : null;
  }
  function setConsent(decision) {
    if (decision !== 'granted' && decision !== 'denied') return;
    safeSet(LS.CONSENT, { decision: decision, at: Date.now() });
    document.dispatchEvent(new CustomEvent('sc-taste:consent', { detail: { decision: decision } }));
    if (decision === 'denied') clearProfile();
  }
  function clearProfile() {
    safeRemove(LS.VIEWS);
    safeRemove(LS.QUERIES);
    safeRemove(LS.COLLECTIONS);
  }

  /* ------------------------------------------------------------------------
     Trackers — only write when consent granted.
  ------------------------------------------------------------------------ */
  function trackView(p) {
    if (hasConsent() !== 'granted') return;
    if (!p || !p.handle) return;
    var list = safeGet(LS.VIEWS) || [];
    // Remove any prior entry for this handle so we promote it to most-recent
    list = list.filter(function(x){ return x.handle !== p.handle; });
    list.unshift({
      handle: p.handle,
      id: p.id,
      tags: (p.tags || []).slice(0, 30),
      type: p.type || '',
      vendor: p.vendor || '',
      price: p.price || 0,
      collections: (p.collections || []).slice(0, 6),
      at: Date.now()
    });
    if (list.length > LIMITS.VIEWS) list = list.slice(0, LIMITS.VIEWS);
    safeSet(LS.VIEWS, list);
  }
  function trackSearch(query) {
    if (hasConsent() !== 'granted') return;
    if (!query) return;
    var q = String(query).trim().toLowerCase();
    if (!q || q.length < 2 || q.length > 80) return;
    var list = (safeGet(LS.QUERIES) || []).filter(function(x){
      return x.q !== q && (Date.now() - x.at) < QUERY_TTL_MS;
    });
    list.unshift({ q: q, at: Date.now() });
    if (list.length > LIMITS.QUERIES) list = list.slice(0, LIMITS.QUERIES);
    safeSet(LS.QUERIES, list);
  }
  function trackCollection(handle) {
    if (hasConsent() !== 'granted') return;
    if (!handle) return;
    var list = (safeGet(LS.COLLECTIONS) || []).filter(function(x){
      return x.handle !== handle;
    });
    list.unshift({ handle: handle, at: Date.now() });
    if (list.length > LIMITS.COLLECTIONS) list = list.slice(0, LIMITS.COLLECTIONS);
    safeSet(LS.COLLECTIONS, list);
  }

  /* ------------------------------------------------------------------------
     Profile aggregator — builds a lightweight taste fingerprint.
  ------------------------------------------------------------------------ */
  function profile() {
    var views  = safeGet(LS.VIEWS) || [];
    var queries = (safeGet(LS.QUERIES) || []).map(function(x){ return x.q; });
    var colls  = (safeGet(LS.COLLECTIONS) || []).map(function(x){ return x.handle; });

    // Tag frequency, weighted by recency.
    var tagScores = {};
    var typeScores = {};
    var vendorScores = {};
    var priceSamples = [];
    views.forEach(function(v, idx){
      var decay = Math.pow(0.9, idx); // newer weighs more
      (v.tags || []).forEach(function(t){
        var tlc = String(t).toLowerCase();
        tagScores[tlc] = (tagScores[tlc] || 0) + decay;
      });
      if (v.type)   typeScores[v.type]   = (typeScores[v.type]   || 0) + decay;
      if (v.vendor) vendorScores[v.vendor] = (vendorScores[v.vendor] || 0) + decay;
      if (typeof v.price === 'number' && v.price > 0) priceSamples.push(v.price);
    });

    var medianPrice = 0;
    if (priceSamples.length) {
      var sorted = priceSamples.slice().sort(function(a,b){return a-b;});
      medianPrice = sorted[Math.floor(sorted.length / 2)];
    }

    return {
      hasData: views.length > 0 || queries.length > 0 || colls.length > 0,
      viewHandles: views.map(function(v){ return v.handle; }),
      recentCollections: colls,
      queries: queries,
      tagScores: tagScores,
      typeScores: typeScores,
      vendorScores: vendorScores,
      medianPrice: medianPrice,
      viewCount: views.length
    };
  }

  /* ------------------------------------------------------------------------
     Fetchers — wrap Shopify's AJAX endpoints.
  ------------------------------------------------------------------------ */
  function recoIntent(productId, intent, limit) {
    var url = '/recommendations/products.json?product_id=' + encodeURIComponent(productId) +
              '&intent=' + encodeURIComponent(intent) +
              '&limit=' + (limit || 10);
    return fetch(url, { credentials: 'same-origin' })
      .then(function(r){ return r.ok ? r.json() : { products: [] }; })
      .then(function(j){ return (j && j.products) || []; })
      .catch(function(){ return []; });
  }

  function collectionProducts(handle, limit) {
    if (!handle) return Promise.resolve([]);
    var url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + (limit || 24);
    return fetch(url, { credentials: 'same-origin' })
      .then(function(r){ return r.ok ? r.json() : { products: [] }; })
      .then(function(j){ return (j && j.products) || []; })
      .catch(function(){ return []; });
  }

  function productByHandle(handle) {
    if (!handle) return Promise.resolve(null);
    var url = '/products/' + encodeURIComponent(handle) + '.json';
    return fetch(url, { credentials: 'same-origin' })
      .then(function(r){ return r.ok ? r.json() : null; })
      .then(function(j){ return j && j.product ? j.product : null; })
      .catch(function(){ return null; });
  }

  function cartSnapshot() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r){ return r.ok ? r.json() : { items: [] }; })
      .then(function(j){ return (j && j.items) || []; })
      .catch(function(){ return []; });
  }

  /* ------------------------------------------------------------------------
     Furnishings family map — used by the "Pairs Well With" filter.
     Maps a Shopify product_type string (or any text) into one of a small
     set of broad families. Two products in the same family are NOT
     complementary (a chair and a loveseat are both "seating", so they
     shouldn't pair with each other on a chair PDP). Two products in
     DIFFERENT families ARE complementary (chair + lamp = ok).
     Add new patterns here as the catalog grows.
  ------------------------------------------------------------------------ */
  function productFamily(typeStr) {
    var t = String(typeStr || '').toLowerCase();
    if (!t) return '';
    // ORDER MATTERS — most-specific patterns first so "Table Lamp" hits
    // 'lighting' (not 'tables'), "Coffee Table" hits 'tables' (not 'coffee'),
    // and so on. Specific words (lamp, sconce, espresso) before generic ones.
    if (/(lamp|sconce|chandelier|lantern|fixture|pendant\s|^pendant$|\blight\b|lighting)/.test(t)) return 'lighting';
    if (/(coffee.?table|side.?table|end.?table|console|dining.?table|nightstand|desk|\btable\b)/.test(t)) return 'tables';
    if (/(chair|lounge|sofa|loveseat|sectional|ottoman|bench|stool|daybed|recliner|seat\b|settee)/.test(t)) return 'seating';
    if (/(cabinet|dresser|shelving|shelf|sideboard|wardrobe|armoire|credenza|storage)/.test(t)) return 'storage';
    if (/(\bbed\b|headboard|bed.?frame)/.test(t)) return 'beds';
    if (/(rug\b|carpet)/.test(t)) return 'rugs';
    if (/(throw|blanket|quilt|coverlet|pillow|cushion|curtain|drape|textile|linen)/.test(t)) return 'textiles';
    if (/(candle|incense|fragrance|diffuser)/.test(t)) return 'fragrance';
    if (/(wallpaper|wallcovering)/.test(t)) return 'wallpaper';
    if (/(hardware|knob|pull|hook|handle)/.test(t)) return 'hardware';
    if (/(\btea\b|matcha|tisane|infusion)/.test(t)) return 'tea';
    if (/(coffee|espresso|cold.?brew)/.test(t)) return 'coffee';
    if (/(kettle|teapot|french.?press|moka|\bcup\b|\bmug\b|tumbler|carafe|pitcher|tray|spoon|scoop|strainer|filter|kitchen|cookware|cutting.?board)/.test(t)) return 'kitchen';
    if (/(supplement|vitamin|tonic|tincture|bath|body|skincare|skin.?care|soap|lotion|balm|serum|wellness|self.?care)/.test(t)) return 'wellness';
    if (/(book|magazine|journal|notebook|stationery|paper)/.test(t)) return 'paper';
    if (/(dress|shirt|top\b|tee\b|pants|skirt|jacket|coat|sweater|cardigan|jumpsuit|romper|blouse|tunic|kimono|robe|swim|underwear|sock|shoe|sandal|boot|sneaker|belt|hat|scarf|glove|apparel|clothing|fashion)/.test(t)) return 'apparel';
    if (/(bracelet|necklace|\bring\b|earring|jewelry|jewellery|cuff|bangle|brooch|anklet)/.test(t)) return 'jewelry';
    if (/(bag\b|tote|wallet|backpack|purse|clutch|crossbody)/.test(t)) return 'bags';
    if (/(art|print|mirror|vase|vessel|sculpture|object|d[eé]cor|bowl|ice.?bucket|planter)/.test(t)) return 'decor';
    return t; // unknown — fall back to raw type so identical types still match
  }

  // Detect a product's family using all available signals. product_type is
  // best when present, but admin sometimes leaves it blank. Falls back to
  // title text, then tags. Keeps the engine working on stores where types
  // aren't consistently set.
  function detectFamily(input) {
    if (!input) return '';
    var pt = input.product_type || input.type || '';
    var fam = productFamily(pt);
    if (fam && fam !== String(pt).toLowerCase()) return fam;
    // Title fallback
    fam = productFamily(input.title || '');
    if (fam && fam !== String(input.title || '').toLowerCase()) return fam;
    // Tags fallback (joined into one big haystack)
    var tags = input.tags;
    if (tags) {
      var tagStr = Array.isArray(tags) ? tags.join(' ') : String(tags);
      fam = productFamily(tagStr);
      if (fam && fam !== tagStr.toLowerCase()) return fam;
    }
    return String(pt).toLowerCase(); // last resort: raw type
  }

  // Anything in this set is a furnishings product. Used to gate out
  // apparel / jewelry / bags etc. from rails that anchor on furnishings.
  var FURNISHINGS_FAMILIES = {
    seating: 1, tables: 1, storage: 1, beds: 1, lighting: 1,
    rugs: 1, textiles: 1, decor: 1, fragrance: 1, wallpaper: 1, hardware: 1
  };
  var NON_FURNISHINGS_FAMILIES = { apparel: 1, jewelry: 1, bags: 1 };

  /* ------------------------------------------------------------------------
     Complementary affinity map — for each family, the OTHER families that
     genuinely pair with it on a PDP "More to Explore" rail.

     Reading: when the current product is in family X, candidates whose
     family is in COMPLEMENTARY_FAMILIES[X] are shown; everything else is
     rejected. This is what stops "face care with light bulbs" — wellness
     is not in lighting's complementary list.

     A family appearing in its OWN list (e.g. lighting → ['lighting', ...])
     means same-family is allowed (a bulb pairs with a fixture). A family
     missing from its own list means duplicates are not shown (tea on a
     tea PDP doesn't show more tea).
  ------------------------------------------------------------------------ */
  var COMPLEMENTARY_FAMILIES = {
    // Home / interiors — bulbs pair with fixtures AND with the rest of the room.
    lighting:  ['lighting', 'seating', 'tables', 'storage', 'beds', 'rugs', 'textiles', 'decor', 'hardware', 'wallpaper'],
    seating:   ['lighting', 'tables', 'rugs', 'textiles', 'decor', 'hardware', 'wallpaper'],
    tables:    ['lighting', 'seating', 'rugs', 'textiles', 'decor', 'hardware', 'wallpaper'],
    storage:   ['lighting', 'rugs', 'textiles', 'decor', 'hardware', 'wallpaper'],
    beds:      ['lighting', 'rugs', 'textiles', 'decor', 'storage', 'hardware', 'wallpaper'],
    rugs:      ['seating', 'tables', 'lighting', 'storage', 'beds', 'textiles', 'decor'],
    textiles:  ['seating', 'beds', 'tables', 'lighting', 'rugs', 'decor', 'textiles', 'wallpaper'],
    decor:     ['seating', 'tables', 'lighting', 'storage', 'beds', 'rugs', 'textiles', 'fragrance', 'decor', 'wallpaper'],
    fragrance: ['decor', 'textiles', 'beds', 'wellness', 'kitchen', 'fragrance'],
    wallpaper: ['lighting', 'seating', 'tables', 'decor', 'hardware', 'textiles'],
    hardware:  ['lighting', 'seating', 'tables', 'storage', 'beds', 'wallpaper'],
    // Kitchen / consumables — coffee + tea share the kitchen group.
    tea:       ['kitchen', 'coffee', 'fragrance', 'paper'],
    coffee:    ['kitchen', 'tea', 'fragrance', 'paper'],
    kitchen:   ['tea', 'coffee', 'fragrance', 'kitchen'],
    // Wellness — pairs with sensory + soft goods.
    wellness:  ['fragrance', 'textiles', 'wellness', 'paper'],
    // Paper / books — quiet companions to consumables and wellness.
    paper:     ['tea', 'coffee', 'wellness', 'paper'],
    // Apparel — its own ecosystem.
    apparel:   ['jewelry', 'bags', 'apparel'],
    jewelry:   ['apparel', 'bags', 'jewelry'],
    bags:      ['apparel', 'jewelry']
  };

  /* ------------------------------------------------------------------------
     Scoring — assign a numeric score to each candidate.
  ------------------------------------------------------------------------ */
  function jaccard(a, b) {
    if (!a || !b || !a.length || !b.length) return 0;
    var setA = {};
    var norm = function(s){ return String(s).toLowerCase().trim(); };
    a.forEach(function(x){ setA[norm(x)] = 1; });
    var inter = 0, uni = Object.keys(setA).length;
    b.forEach(function(x){
      var k = norm(x);
      if (setA[k]) inter += 1;
      else uni += 1;
    });
    return uni > 0 ? inter / uni : 0;
  }

  function tagOverlapScore(tagScores, tags) {
    if (!tags || !tags.length) return 0;
    var keys = Object.keys(tagScores || {});
    if (!keys.length) return 0;
    var total = 0, matched = 0;
    tags.forEach(function(t){
      var k = String(t).toLowerCase();
      if (tagScores[k]) matched += tagScores[k];
    });
    keys.forEach(function(k){ total += tagScores[k]; });
    return total > 0 ? matched / total : 0;
  }

  function priceProximityScore(candidatePrice, anchorPrice) {
    if (!candidatePrice || !anchorPrice) return 0;
    var ratio = Math.min(candidatePrice, anchorPrice) / Math.max(candidatePrice, anchorPrice);
    // 1 when prices match, ~0 when an order of magnitude apart
    return ratio;
  }

  function titleMatchesAnyQuery(title, tags, queries) {
    if (!queries || !queries.length) return 0;
    var haystack = (String(title || '') + ' ' + (tags || []).join(' ')).toLowerCase();
    var hits = 0;
    queries.forEach(function(q){
      if (q && haystack.indexOf(q) !== -1) hits += 1;
    });
    return hits;
  }

  function scoreCandidate(candidate, ctx, intent) {
    if (!candidate) return -Infinity;

    // Hard rejects
    var cid = candidate.id;
    var chandle = candidate.handle;
    if (ctx.cartIds && ctx.cartIds[cid]) return -Infinity;
    if (ctx.currentId && ctx.currentId == cid) return -Infinity;

    var cTypeEarly = (candidate.product_type || candidate.type || '').toLowerCase();
    // PAIRS = complementary categories ONLY. Sabai-style stores tag each
    // SKU with its own narrow product_type (Club Chair, Loveseat, Sectional)
    // so an exact-string match is too narrow — they're all "seating" to a
    // human. Map each product_type to a broad furnishings FAMILY and reject
    // candidates whose family matches the current product's family.
    // Use detectFamily on BOTH sides — it falls back to title and tags
    // when product_type is blank. Critical because admin often forgets to
    // set product_type on lighting/decor/etc.
    var currentFamily = detectFamily({
      type: ctx.currentType,
      title: ctx.currentTitle,
      tags: ctx.currentTags
    });
    var candFamily = detectFamily(candidate);

    // UNIVERSE GATE — applies to BOTH 'pairs' and 'more'. If we're on
    // a furnishings PDP (chair, lamp, rug, decor, etc.), never recommend
    // a dress, bracelet, or handbag. The store may carry these but they
    // don't belong in a furnishings-PDP rail.
    if (FURNISHINGS_FAMILIES[currentFamily] && NON_FURNISHINGS_FAMILIES[candFamily]) {
      return -Infinity;
    }

    // Rail filtering:
    //   pairs ('More to Explore') = candidates whose family is in the
    //     current family's COMPLEMENTARY_FAMILIES list (face care won't
    //     show on a light bulb because wellness isn't in lighting's list).
    //   more ('Related Products') = same-family only (lighting + lighting,
    //     seating + seating).
    if (intent === 'pairs' && currentFamily) {
      var allowed = COMPLEMENTARY_FAMILIES[currentFamily];
      if (allowed) {
        // We have an explicit affinity list — enforce it strictly. If the
        // candidate has no detectable family, allow it through ONLY when
        // its tags overlap the allowed set (so junk-typed items can still
        // surface IF they tag themselves correctly).
        if (candFamily) {
          if (allowed.indexOf(candFamily) === -1) return -Infinity;
        } else {
          var cTagsLcP = (candidate.tags || []).map(function(t){ return String(t).toLowerCase(); });
          var anyMatch = allowed.some(function(f){ return cTagsLcP.indexOf(f) !== -1; });
          if (!anyMatch) return -Infinity;
        }
      } else {
        // Unknown current family — fall back to old "no same-family" rule.
        if (candFamily && candFamily === currentFamily) return -Infinity;
      }
    }
    if (intent === 'more' && currentFamily) {
      if (candFamily && candFamily !== currentFamily) return -Infinity;
    }

    var s = 0;
    var cTags = (candidate.tags || []).map(function(t){ return String(t).toLowerCase(); });
    var cType = (candidate.product_type || candidate.type || '').toLowerCase();
    var cVendor = (candidate.vendor || '').toLowerCase();

    // 1) Jaccard overlap with CURRENT product
    if (ctx.currentTags) s += 2.0 * jaccard(ctx.currentTags, cTags);

    // 2) Shared collection with current product
    if (ctx.currentCollections && candidate._collections) {
      var shared = 0;
      ctx.currentCollections.forEach(function(h){
        if (candidate._collections.indexOf(h) !== -1) shared += 1;
      });
      s += 1.5 * Math.min(shared, 3);
    }

    // 3) Same vendor / type vs. current product
    var sameVendorBonus = intent === 'more' ? 0.5 : 0.2;
    var sameTypeBonus   = intent === 'pairs' ? 0.1 : 0.9;      // "pairs" rewards CROSS-type
    var crossTypeBonus  = intent === 'pairs' ? 0.7 : 0;
    if (ctx.currentVendor && cVendor === ctx.currentVendor) s += sameVendorBonus;
    if (ctx.currentType) {
      if (cType === ctx.currentType) s += sameTypeBonus;
      else                            s += crossTypeBonus;
    }

    // 4) Taste-profile overlap (tag frequency, type affinity, vendor affinity)
    s += 1.2 * tagOverlapScore(ctx.profile.tagScores, cTags);
    if (cType && ctx.profile.typeScores[cType])     s += 0.4;
    if (cVendor && ctx.profile.vendorScores[cVendor]) s += 0.2;

    // 5) Price proximity — to current product, then to profile median
    var cPrice = candidate._priceCents || candidate.price || 0;
    if (cPrice && ctx.currentPriceCents) s += 0.5 * priceProximityScore(cPrice, ctx.currentPriceCents);
    if (cPrice && ctx.profile.medianPrice) s += 0.4 * priceProximityScore(cPrice, ctx.profile.medianPrice);

    // 6) Search-query text match (strong boost when the visitor searched it)
    var qHits = titleMatchesAnyQuery(candidate.title, cTags, ctx.profile.queries);
    s += 1.5 * Math.min(qHits, 3);

    // 7) Penalties
    if (ctx.recentHandles && ctx.recentHandles.indexOf(chandle) !== -1) s -= 0.35;
    if (ctx.wishlistIds && ctx.wishlistIds[cid]) s -= 0.3;
    if (candidate.available === false) s -= 1.5;

    // 8) Boosts
    if (cTags.indexOf('new') !== -1) s += 0.25;
    if (candidate._isPin) s += 2.0; // merchant-pinned via metafield

    return s;
  }

  /* ------------------------------------------------------------------------
     Candidate pool assembly
  ------------------------------------------------------------------------ */
  function dedupe(products) {
    var seen = {};
    var out = [];
    products.forEach(function(p){
      if (!p) return;
      var key = p.id || p.handle;
      if (!key || seen[key]) return;
      seen[key] = 1;
      out.push(p);
    });
    return out;
  }

  function wishlistIdsFromGlobal() {
    // Read whatever the existing wishlist module exposes. Fall back to empty.
    try {
      if (window.Socius && window.Socius.wishlist && window.Socius.wishlist.variantIds) {
        return window.Socius.wishlist.variantIds();
      }
    } catch (e) {}
    return {};
  }

  /**
   * Build and rank a rail.
   * opts = {
   *   intent: 'pairs' | 'more' | 'explore',
   *   productId, productHandle,
   *   productContext: { tags, type, vendor, priceCents, collections },
   *   pins: [productHandle, ...]      // merchant metafield pins (optional)
   *   fallbackCollection: 'bestsellers',
   *   limit: 8
   * }
   */
  function getRail(opts) {
    opts = opts || {};
    var intent = opts.intent || 'more';
    var limit = opts.limit || 8;
    var ctx = opts.productContext || {};
    var pins = opts.pins || [];
    var fallbackCollection = opts.fallbackCollection || '';
    var prof = profile();

    // Which Shopify intents to call — 'pairs' uses 'complementary', 'more' uses 'related',
    // 'explore' uses 'related' with no current product (i.e. profile-only).
    var shopifyIntent = (intent === 'pairs') ? 'complementary' : 'related';

    var candidatePromises = [];

    // Pool sizes upped 2026-05-02 — rails were rendering only ~6 cards
    // because the candidate pool was too thin after family + dedupe filters.
    if (opts.productId && intent !== 'explore') {
      candidatePromises.push(recoIntent(opts.productId, shopifyIntent, 10));
      var other = shopifyIntent === 'complementary' ? 'related' : 'complementary';
      candidatePromises.push(recoIntent(opts.productId, other, 10));
    }

    // Merchant pins
    if (pins.length) {
      pins.slice(0, 10).forEach(function(h){
        candidatePromises.push(productByHandle(h).then(function(p){
          if (p) p._isPin = true;
          return p ? [p] : [];
        }));
      });
    }

    // Current product's collections — top 3
    if (ctx.collections && ctx.collections.length) {
      ctx.collections.slice(0, 3).forEach(function(h){
        candidatePromises.push(collectionProducts(h, 30).then(function(list){
          list.forEach(function(p){
            p._collections = p._collections || [];
            if (p._collections.indexOf(h) === -1) p._collections.push(h);
          });
          return list;
        }));
      });
    }

    // Visitor's recently-viewed collections — cross-pollinate taste
    prof.recentCollections.slice(0, 3).forEach(function(h){
      if (ctx.collections && ctx.collections.indexOf(h) !== -1) return;
      candidatePromises.push(collectionProducts(h, 24).then(function(list){
        list.forEach(function(p){
          p._collections = p._collections || [];
          if (p._collections.indexOf(h) === -1) p._collections.push(h);
        });
        return list;
      }));
    });

    // Cold-start / last-resort: curated bestsellers (much bigger pull
    // so it can fill any rail that's starved by tight filtering).
    if (fallbackCollection) {
      candidatePromises.push(collectionProducts(fallbackCollection, 50));
    }

    // Final safety net — pull from /products.json (newest 50) so we
    // always have SOMETHING to show even on stores without a curated
    // bestsellers collection.
    candidatePromises.push(
      fetch('/products.json?limit=50', { credentials: 'same-origin' })
        .then(function(r){ return r.ok ? r.json() : { products: [] }; })
        .then(function(j){ return (j && j.products) || []; })
        .catch(function(){ return []; })
    );

    // Cart contents (for penalty list)
    candidatePromises.push(cartSnapshot().then(function(items){
      return { _cart: items };
    }));

    return Promise.all(candidatePromises).then(function(pools){
      var cartIds = {};
      var rawCandidates = [];

      pools.forEach(function(pool){
        if (!pool) return;
        if (pool._cart) {
          pool._cart.forEach(function(i){ if (i && i.product_id) cartIds[i.product_id] = 1; });
          return;
        }
        (pool || []).forEach(function(p){
          if (!p) return;
          // Normalize price to cents if Shopify returned dollars (product.json uses the shop's price format,
          // usually a string like "39.00" in dollars). Our intent JSON returns cents. We detect:
          if (p.price != null && typeof p.price === 'string') {
            var cents = Math.round(parseFloat(p.price) * 100);
            if (!isNaN(cents)) p._priceCents = cents;
          } else if (typeof p.price === 'number') {
            p._priceCents = p.price; // intent JSON returns cents as integer
          }
          rawCandidates.push(p);
        });
      });

      var candidates = dedupe(rawCandidates);

      var scoringCtx = {
        profile: prof,
        currentId:        opts.productId || null,
        currentTitle:     ctx.title || '',
        currentTags:      ctx.tags || [],
        currentType:      (ctx.type || '').toLowerCase(),
        currentVendor:    (ctx.vendor || '').toLowerCase(),
        currentCollections: ctx.collections || [],
        currentPriceCents: ctx.priceCents || 0,
        cartIds:          cartIds,
        wishlistIds:      wishlistIdsFromGlobal(),
        recentHandles:    prof.viewHandles
      };

      // Score & sort
      candidates.forEach(function(p){ p._score = scoreCandidate(p, scoringCtx, intent); });

      // Tie-breaker: when two candidates score equally (siblings of the
      // same product line typically do), order by a deterministic hash of
      // (currentId XOR candidateId). That way each sibling product (Bacana
      // in Hemp vs. Bacana in Velvet) sees a slightly DIFFERENT ordering
      // so its rail isn't a carbon copy of the previous product's rail.
      var anchorId = parseInt(opts.productId || 0, 10) || 0;
      candidates.sort(function(a, b){
        if (b._score !== a._score) return b._score - a._score;
        var ah = ((parseInt(a.id, 10) || 0) ^ anchorId) >>> 0;
        var bh = ((parseInt(b.id, 10) || 0) ^ anchorId) >>> 0;
        return ah - bh;
      });

      // Minimum score floor — don't surface anything with a terrible score
      var filtered = candidates.filter(function(p){ return p._score > -Infinity; });

      // Title-family dedupe — collapse linked-product siblings of the
      // RECOMMENDATION (e.g. "Mantle Lamp in Yellow", "Mantle Lamp in
      // Orange", etc. all become just one "Mantle Lamp" tile). Uses the
      // " in " split convention from the catalog naming.
      function titleFamily(p) {
        var t = String(p.title || '').toLowerCase().trim();
        var idx = t.indexOf(' in ');
        return idx > 0 ? t.substring(0, idx) : t;
      }
      var familySeen = {};
      filtered = filtered.filter(function(p) {
        var fam = titleFamily(p);
        if (!fam) return true;
        if (familySeen[fam]) return false;
        familySeen[fam] = 1;
        return true;
      });

      return filtered.slice(0, limit);
    });
  }

  /* ------------------------------------------------------------------------
     Auto-hook search inputs on the page — predictive search forms use input
     with name=q. We capture the final query on Enter / submit.
  ------------------------------------------------------------------------ */
  function bindSearchInputs() {
    document.querySelectorAll('input[name="q"]').forEach(function(input){
      if (input.dataset.scTasteBound === '1') return;
      input.dataset.scTasteBound = '1';
      var form = input.closest('form');
      if (form) {
        form.addEventListener('submit', function(){
          trackSearch(input.value);
        }, { capture: true });
      }
      input.addEventListener('keydown', function(e){
        if (e.key === 'Enter') trackSearch(input.value);
      });
    });
    // URL param ?q= (search results landing)
    try {
      var url = new URL(window.location.href);
      var q = url.searchParams.get('q');
      if (q) trackSearch(q);
    } catch (e) {}
  }

  /* ------------------------------------------------------------------------
     Public surface
  ------------------------------------------------------------------------ */
  window.SocusTaste = {
    version:      VERSION,
    hasConsent:   hasConsent,
    setConsent:   setConsent,
    trackView:    trackView,
    trackSearch:  trackSearch,
    trackCollection: trackCollection,
    profile:      profile,
    getRail:      getRail,
    clearProfile: clearProfile,
    _fetchers: { recoIntent: recoIntent, collectionProducts: collectionProducts, productByHandle: productByHandle }
  };

  /* ------------------------------------------------------------------------
     Init
  ------------------------------------------------------------------------ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindSearchInputs);
  } else {
    bindSearchInputs();
  }
})();